import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CAMPO, Recado } from "./ui";
import {
  definirResponsavel,
  divulgarAtualizacao,
  gerarDivulgacao,
  lerConteudoDaFerramenta,
  retirarDivulgacao,
  type ConteudoDaFerramenta,
  lerDivulgacao,
  lerHistoricoDoCard,
  mensagemDoCatalogo,
  lerAtualizacoes,
  registrarAtualizacao,
  removerAtualizacao,
  type AtualizacaoDaFerramenta,
  salvarCard,
  type CardDoCatalogo,
  type Catalogo,
  type DivulgacaoDaFerramenta,
  type MudancaDaFerramenta,
} from "../lib/adminCatalogo";
import { aberturaDoCard, type CardVitrine } from "../lib/dados";
import { lerUsoDasFerramentas, type UsoDaFerramenta } from "../lib/uso";
import {
  camposDaSituacao,
  Prosa,
  SITUACOES,
  situacaoDaFerramenta,
  type ChaveDaSituacao,
} from "./AdminCatalogoUi";

/**
 * Admin › Catálogo › Vitrine › **a ferramenta** (000257).
 *
 * ---------------------------------------------------------------------------
 * Por que uma tela dedicada, e não o formulário embutido que havia antes
 * ---------------------------------------------------------------------------
 * Pedido de Victor em 16/09: *"deve ser possível abrir cada ferramenta e editar
 * em uma tela dedicada"*. O formulário que morava dentro da Vitrine saiu no
 * mesmo ato, e a remoção é a parte que importa: duas telas editando o mesmo
 * card seriam **duas portas para a mesma ação**, e a segunda porta é a que fica
 * desatualizada — é a razão de não existir aba "Acessos" neste painel.
 *
 * ---------------------------------------------------------------------------
 * DOIS textos, com dois públicos, e a tela diz qual é qual
 * ---------------------------------------------------------------------------
 *   · **Descrição** é a linha que o ALUNO lê embaixo do nome, na vitrine do
 *     curso dele. Aparece em três lugares de `Curso.tsx`, e 31 dos 33 cards já
 *     a tinham escrita.
 *   · **O que é esta ferramenta** (`cards.sobre`, 000257) é para o OPERADOR:
 *     documentação interna, que nenhum aluno vê.
 *
 * Elas nasceram separadas de propósito. Um campo só faria um texto escrito para
 * quem opera aparecer na tela de 29.884 alunos da Escola no instante em que
 * alguém salvasse — e no dia em que os dois divergissem, quem escreve um
 * estragaria o outro sem ver.
 *
 * ---------------------------------------------------------------------------
 * O alcance não é deste curso
 * ---------------------------------------------------------------------------
 * O card é UM só e serve todo produto que o liga, desde a 000064. Quem chegou
 * aqui veio de um curso e não tem como saber disso sozinho — então a tela conta,
 * com o número na frente, antes de qualquer campo.
 *
 * ---------------------------------------------------------------------------
 * Os OITO campos da ficha, num lugar só (21/09)
 * ---------------------------------------------------------------------------
 * A equipe de produtos mantém no ClickUp a tabela do que toda ferramenta
 * precisa ter, e Victor pediu que o time preenchesse tudo aqui. Onde cada um
 * mora, porque nenhum deles é campo novo por capricho:
 *
 *   nome, descrição, link de acesso   · «O que o aluno vê»
 *   o que a ferramenta é (interno)    · «O que é esta ferramenta»
 *   produtos vinculados               · o ALCANCE, no alto (muda-se na lista)
 *   responsável                       · «Quem mantém»
 *   status                            · «Quem mantém» — um select que TRADUZ
 *   data da última atualização  ┐     · «A última atualização» (000268), as duas
 *   o que mudou nessa atualização ┘     num ato só, com RPC própria
 *
 * E desde 25/09 (000299), dois blocos que a ficha não tinha:
 *
 *   · o LINK DE ACESSO (F2) — as ferramentas que moram aqui não têm `url`, e
 *     o campo "Endereço" ficava vazio para as vinte. O link é DERIVADO pela
 *     mesma função que a vitrine do aluno usa (`aberturaDoCard`), nunca
 *     remontado: a primeira diferença entre os dois seria um link que o painel
 *     mostra e o aluno não recebe;
 *   · o HISTÓRICO DE MUDANÇAS (F7) — o razão do catálogo desta ferramenta.
 *     Não é o de «Atualizações»: aquele é o que mudou na FERRAMENTA, contado
 *     por uma pessoa; este é quem mudou o CADASTRO neste painel, gravado pelo
 *     banco. A aba global "Mudanças" continua fora, por decisão de Victor.
 *
 * O status é o único que não tem coluna: ele é derivado de `ativo` e `destino`,
 * e o select escreve esses dois. Ver `camposDaSituacao` em `AdminCatalogoUi`.
 */

const DESTINOS = ["em-breve", "url", "interno", "memberkit", "sso-cfc", "sso-sentinela"] as const;

type Rascunho = {
  nome: string;
  descricao: string;
  sobre: string;
  destino: string;
  url: string;
  ordem: string;
  /**
   * A SITUAÇÃO escolhida. Ela substituiu a caixinha "Card ativo": o que se
   * salva continua sendo `ativo` + `destino`, e `camposDaSituacao` faz a
   * tradução no momento de salvar.
   *
   * Ela é campo do rascunho, e não uma derivação do rascunho, de propósito:
   * derivada, ela voltaria sozinha para "em construção" no instante em que
   * alguém escolhesse "ativa" num card cujo destino ainda é `em-breve` — e o
   * operador leria isso como o select não funcionando. Do jeito que está, os
   * dois ficam divergentes por um momento e a tela DIZ o que falta.
   */
  situacao: ChaveDaSituacao;
  motivo: string;
};

function doCard(c: CardDoCatalogo): Rascunho {
  return {
    nome: c.nome,
    descricao: c.descricao ?? "",
    sobre: c.sobre ?? "",
    destino: c.destino,
    url: c.url ?? "",
    ordem: String(c.ordem),
    situacao: situacaoDaFerramenta(c).chave,
    motivo: "",
  };
}

export default function AdminCatalogoFerramenta({
  catalogo,
  recarregar,
  slug,
  voltar,
  aviso = null,
}: {
  catalogo: Catalogo;
  recarregar: () => Promise<void>;
  slug: string;
  /** O caminho de volta para a Vitrine, com o curso de onde a pessoa veio. */
  voltar: string;
  /**
   * A frase do banco sobre a ferramenta que acabou de NASCER (000299). Ela diz
   * o próximo passo — ligar num curso — e por que a ferramenta ainda não abre,
   * e por isso viaja da lista até aqui em vez de sumir com a navegação.
   */
  aviso?: string | null;
}) {
  const card = catalogo.cards.find((c) => c.slug === slug) ?? null;

  // Nenhum hook depois de um `return` condicional: um `useState` fora de ordem
  // derruba a rota inteira com o erro React #310, e nem o `tsc` nem a suíte
  // veem isso. Por isso o rascunho nasce de um card que pode ser nulo.
  const [rascunho, setRascunho] = useState<Rascunho>(() =>
    card
      ? doCard(card)
      : {
          nome: "",
          descricao: "",
          sobre: "",
          destino: "em-breve",
          url: "",
          ordem: "",
          situacao: "em-construcao",
          motivo: "",
        },
  );
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const primeiro = useRef<HTMLInputElement | null>(null);

  // O responsável tem estado e botão PRÓPRIOS, porque é RPC própria: definir
  // dono é outro ato, com outra linha no razão, e quem salva o texto não
  // deveria estar decidindo isso junto sem perceber.
  const [dono, setDono] = useState(card?.responsavel ?? "");
  const [salvandoDono, setSalvandoDono] = useState(false);
  const [recadoDono, setRecadoDono] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  // A atualização é RPC própria pela mesma razão, e as duas partes dela vivem
  // num estado só: elas são um ato, e separá-las deixaria a tela mostrar uma
  // data com a nota de outra atualização embaixo.
  //
  // **O formulário nasce VAZIO desde 22/09**, e isso é a mudança de sentido que
  // o histórico trouxe: ele deixou de EDITAR a última atualização e passou a
  // ACRESCENTAR uma. Nascer preenchido com a anterior faria quem só quisesse
  // conferir a data salvar uma cópia dela por engano.
  const [atualizacao, setAtualizacao] = useState({ quando: "", nota: "" });
  const [salvandoAtualizacao, setSalvandoAtualizacao] = useState(false);
  const [recadoAtualizacao, setRecadoAtualizacao] = useState<{
    tipo: "ok" | "erro";
    texto: string;
  } | null>(null);

  // O HISTÓRICO (000277) vem de RPC própria, pela mesma razão do documento de
  // divulgação: ele interessa a ESTA tela, e pô-lo no catálogo faria as outras
  // três pagarem por ele.
  const [historico, setHistorico] = useState<AtualizacaoDaFerramenta[]>([]);
  const [removendo, setRemovendo] = useState<{ id: string; motivo: string } | null>(null);
  const [salvandoRemocao, setSalvandoRemocao] = useState(false);

  // A DIVULGAÇÃO ao aluno (000304, o F4). Um ato aberto por vez, como a
  // remoção: dois formulários abertos na mesma tabela fariam a pessoa confirmar
  // um achando que confirmava o outro. A `versao` relê o histórico, que a
  // divulgação muda sem mudar a data da ferramenta (a chave do efeito de baixo).
  const [divulgando, setDivulgando] = useState<{ id: string; texto: string } | null>(null);
  const [retirando, setRetirando] = useState<{ id: string; motivo: string } | null>(null);
  const [salvandoDivulgacao, setSalvandoDivulgacao] = useState(false);
  const [recadoDivulgacao, setRecadoDivulgacao] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [versaoAtualizacoes, setVersaoAtualizacoes] = useState(0);

  // A DATA AUTOMÁTICA do conteúdo (000304, o F5): quando as tabelas de conteúdo
  // desta ferramenta mudaram pela última vez. Ela ajuda quem registra — não
  // substitui o registro, porque o QUE mudou não tem fonte automática.
  const [conteudo, setConteudo] = useState<ConteudoDaFerramenta | null>(null);

  // O documento de divulgação NÃO vem em `cat_admin_catalogo`: são 33 textos de
  // alguns milhares de caracteres, e aquela leitura roda a cada troca de
  // sub-aba (a cicatriz da 000254). Ele é lido aqui, uma vez por ferramenta.
  const [doc, setDoc] = useState<DivulgacaoDaFerramenta | null>(null);
  const [gerando, setGerando] = useState(false);
  const [recadoDoc, setRecadoDoc] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [copiou, setCopiou] = useState(false);

  // O HISTÓRICO DE MUDANÇAS (000299) é relido a cada vez que o catálogo do pai
  // muda — todo salvamento desta tela termina em `recarregar()` — e quando o
  // documento é gerado, que não recarrega o catálogo. Sem a `versao`, a linha
  // da divulgação só apareceria na próxima visita.
  const [mudancas, setMudancas] = useState<MudancaDaFerramenta[] | null>(null);
  const [totalDeMudancas, setTotalDeMudancas] = useState(0);
  const [versao, setVersao] = useState(0);

  // Trocar de ferramenta pela URL (o operador volta e abre outra) recarrega o
  // rascunho. Sem isto a tela mostraria os campos da ferramenta ANTERIOR, que é
  // o defeito de campo controlado que guarda a própria formatação.
  useEffect(() => {
    if (card) setRascunho(doCard(card));
    // O campo do dono acompanha o valor DE FORA pela mesma razão do
    // `CampoMoeda` das calculadoras: componente controlado que guarda a própria
    // cópia do valor deixa na tela, depois de um salvamento, um texto que já
    // não é o do banco.
    setDono(card?.responsavel ?? "");
    // Vazio, sempre: o formulário ACRESCENTA ao histórico, não edita a última.
    setAtualizacao({ quando: "", nota: "" });
    setRemovendo(null);
    setDivulgando(null);
    setRetirando(null);
    setRecado(null);
    setRecadoDono(null);
    setRecadoAtualizacao(null);
    setRecadoDivulgacao(null);
  }, [card?.id, card?.responsavel, card?.atualizada_em, card?.atualizacao_nota]); // eslint-disable-line react-hooks/exhaustive-deps

  // O histórico é lido por SLUG, e recarregado quando a ferramenta muda — a
  // mesma razão do documento: sem isto a tabela mostraria a história da
  // ferramenta ANTERIOR, que é pior que mostrar vazio.
  useEffect(() => {
    let vivo = true;
    void (async () => {
      const r = await lerAtualizacoes(slug);
      if (vivo) setHistorico(r.ok ? (r.atualizacoes ?? []) : []);
    })();
    return () => {
      vivo = false;
    };
  }, [slug, card?.atualizada_em, card?.atualizacao_nota, versaoAtualizacoes]);

  useEffect(() => {
    let vivo = true;
    setConteudo(null);
    void (async () => {
      const r = await lerConteudoDaFerramenta(slug);
      if (vivo) setConteudo(r.ok ? r : null);
    })();
    return () => {
      vivo = false;
    };
  }, [slug]);

  // A leitura do documento é por SLUG e não pelo card: ela é a única coisa
  // desta tela que não vem do catálogo do pai, e trocar de ferramenta tem de
  // trocar o documento junto — senão a tela mostra o texto da ANTERIOR, que é
  // o defeito do campo controlado que guarda a própria cópia.
  const recarregarDoc = useCallback(async () => {
    const r = await lerDivulgacao(slug);
    setDoc(r?.ok ? (r as DivulgacaoDaFerramenta) : null);
  }, [slug]);

  useEffect(() => {
    setDoc(null);
    setRecadoDoc(null);
    setCopiou(false);
    void recarregarDoc();
  }, [recarregarDoc]);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      const r = await lerHistoricoDoCard(slug);
      if (!vivo) return;
      setMudancas(r.ok ? (r.mudancas ?? []) : []);
      setTotalDeMudancas(r.ok ? (r.total ?? 0) : 0);
    })();
    return () => {
      vivo = false;
    };
  }, [slug, catalogo, versao]);

  useEffect(() => {
    const t = window.setTimeout(() => primeiro.current?.focus({ preventScroll: true }), 80);
    return () => window.clearTimeout(t);
  }, [slug]);

  if (!card) {
    return (
      <div className="surface rounded-cf-lg p-6" data-ferramenta-inexistente>
        <h2 className="font-display text-lg font-bold text-titulo">Esta ferramenta não existe</h2>
        <p className="mt-2 text-sm text-cinza">
          Nenhum card com o slug <code>{slug}</code> está no catálogo. Ele pode ter sido
          renomeado — nesta casa card não se apaga.
        </p>
        <a href={voltar} className="btn-contorno mt-4 inline-flex min-h-10 items-center text-sm">
          Voltar à Vitrine
        </a>
      </div>
    );
  }

  const cursos = [...new Set(card.produtos.map((p) => p.curso_slug))];
  const situacao = situacaoDaFerramenta(card);

  // As páginas de produto que documentam esta ferramenta — uma por curso que a
  // liga, e só as que de fato têm página. Card universal não tem curso, então
  // não tem página: a lista some em vez de mostrar onze links. Desde a 000306 a
  // página é do HUB (decisão de Victor, 24/09), e o link é para ela, aqui.
  const paginas = card.universal
    ? []
    : cursos
        .map((slug) => catalogo.cursos.find((c) => c.slug === slug))
        .filter((c): c is NonNullable<typeof c> => Boolean(c?.doc))
        .map((c) => ({ curso_slug: c.slug, nome: c.nome }));

  async function salvarDono() {
    setSalvandoDono(true);
    setRecadoDono(null);
    try {
      const r = await definirResponsavel(slug, dono, "definido pelo painel");
      setRecadoDono({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) await recarregar();
    } catch (e) {
      setRecadoDono({
        tipo: "erro",
        texto: mensagemDoCatalogo(null, e instanceof Error ? e.message : undefined),
      });
    } finally {
      setSalvandoDono(false);
    }
  }

  async function salvarAtualizacao() {
    setSalvandoAtualizacao(true);
    setRecadoAtualizacao(null);
    try {
      // A data vai como o campo a entrega (`aaaa-mm-dd`) ou vazia — e vazia
      // deixa o BANCO escolher o dia, no fuso de São Paulo. Calcular "hoje"
      // aqui usaria o relógio do navegador de quem opera, que é o mesmo defeito
      // do carimbo de "calculado em" do IR Proposta.
      const r = await registrarAtualizacao(
        slug,
        atualizacao.nota,
        atualizacao.quando,
        "registrado pelo painel",
      );
      setRecadoAtualizacao({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) {
        // O formulário se esvazia porque o ato ACABOU: o que foi registrado
        // agora aparece na tabela do histórico, embaixo. Deixá-lo preenchido
        // convidaria a salvar a mesma linha duas vezes.
        setAtualizacao({ quando: "", nota: "" });
        await recarregar();
      }
    } catch (e) {
      setRecadoAtualizacao({
        tipo: "erro",
        texto: mensagemDoCatalogo(null, e instanceof Error ? e.message : undefined),
      });
    } finally {
      setSalvandoAtualizacao(false);
    }
  }

  async function removerDoHistorico() {
    if (!removendo) return;
    setSalvandoRemocao(true);
    setRecadoAtualizacao(null);
    try {
      const r = await removerAtualizacao(removendo.id, removendo.motivo.trim());
      setRecadoAtualizacao({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) {
        setRemovendo(null);
        await recarregar();
      }
    } catch (e) {
      setRecadoAtualizacao({
        tipo: "erro",
        texto: mensagemDoCatalogo(null, e instanceof Error ? e.message : undefined),
      });
    } finally {
      setSalvandoRemocao(false);
    }
  }

  async function divulgarAoAluno() {
    if (!divulgando) return;
    setSalvandoDivulgacao(true);
    setRecadoDivulgacao(null);
    try {
      const r = await divulgarAtualizacao(divulgando.id, divulgando.texto);
      setRecadoDivulgacao({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) {
        // O formulário fecha só no SUCESSO: na recusa ele fica, com o que a
        // pessoa escreveu, ao lado da frase do banco dizendo o que corrigir.
        setDivulgando(null);
        setVersaoAtualizacoes((v) => v + 1);
        setVersao((v) => v + 1);
      }
    } catch (e) {
      setRecadoDivulgacao({
        tipo: "erro",
        texto: mensagemDoCatalogo(null, e instanceof Error ? e.message : undefined),
      });
    } finally {
      setSalvandoDivulgacao(false);
    }
  }

  async function retirarDoAluno() {
    if (!retirando) return;
    setSalvandoDivulgacao(true);
    setRecadoDivulgacao(null);
    try {
      const r = await retirarDivulgacao(retirando.id, retirando.motivo.trim());
      setRecadoDivulgacao({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) {
        setRetirando(null);
        setVersaoAtualizacoes((v) => v + 1);
        setVersao((v) => v + 1);
      }
    } catch (e) {
      setRecadoDivulgacao({
        tipo: "erro",
        texto: mensagemDoCatalogo(null, e instanceof Error ? e.message : undefined),
      });
    } finally {
      setSalvandoDivulgacao(false);
    }
  }

  async function gerarODoc() {
    setGerando(true);
    setRecadoDoc(null);
    setCopiou(false);
    try {
      const r = await gerarDivulgacao(slug);
      setRecadoDoc({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      // Relê do BANCO em vez de usar o texto da resposta: é a leitura que traz
      // a procedência e o "desatualizado", e duas fontes para a mesma coisa
      // divergiriam na primeira vez que a função mudasse.
      if (r.ok) {
        await recarregarDoc();
        setVersao((v) => v + 1);
      }
    } catch (e) {
      setRecadoDoc({
        tipo: "erro",
        texto: mensagemDoCatalogo(null, e instanceof Error ? e.message : undefined),
      });
    } finally {
      setGerando(false);
    }
  }

  async function salvar() {
    setSalvando(true);
    setRecado(null);
    try {
      // A SITUAÇÃO escolhida vira os dois campos que mandam. Quem faz a
      // tradução é `camposDaSituacao`, num lugar só — uma segunda cópia dela
      // aqui divergiria da derivação que desenha o chip, e as duas afirmariam
      // coisas diferentes sobre o mesmo card.
      const { ativo, destino } = camposDaSituacao(rascunho.situacao, rascunho.destino);
      const r = await salvarCard({
        slug,
        nome: rascunho.nome,
        descricao: rascunho.descricao,
        sobre: rascunho.sobre,
        destino,
        url: rascunho.url,
        ordem: rascunho.ordem.trim() === "" ? null : Number(rascunho.ordem),
        ativo,
        motivo: rascunho.motivo,
      });
      // A frase é a do BANCO, verbatim. Traduzir `motivo` aqui criaria uma
      // segunda tabela de frases, e a que divergisse em silêncio seria
      // justamente a que ninguém lê.
      setRecado({ tipo: r.ok ? "ok" : "erro", texto: mensagemDoCatalogo(r) });
      if (r.ok) await recarregar();
    } catch (e) {
      setRecado({ tipo: "erro", texto: mensagemDoCatalogo(null, e instanceof Error ? e.message : undefined) });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="space-y-4" data-ferramenta={card.slug}>
      <div className="flex flex-wrap items-center gap-3">
        <a href={voltar} className="btn-contorno inline-flex min-h-10 items-center text-sm" data-voltar>
          ← Vitrine
        </a>
        <p className="text-sm text-cinza">
          {card.tipo} · escopo {card.escopo} · destino {card.destino}
          {card.universal && " · universal"}
        </p>
      </div>

      {aviso && <Recado tipo="ok" texto={aviso} />}

      <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-ferramenta">
        <h2 id="titulo-ferramenta" className="font-display text-xl font-bold text-titulo">
          {card.nome}
          {!card.ativo && (
            <span className="ml-3 rounded-full bg-chip px-2.5 py-1 text-xs font-normal text-chip-tinta">
              desativado
            </span>
          )}
        </h2>
        <p className="mt-1 font-mono text-sm text-cinza">{card.slug}</p>

        {/* O ALCANCE, antes de qualquer campo. O card é um só e serve todo
            produto que o liga (000064): quem chegou aqui veio de um curso e não
            tem como saber disso sozinho. */}
        <p className="mt-4 text-sm text-cinza">
          {card.universal ? (
            <>
              Este card é <strong className="text-tinta">universal</strong>: a vitrine o mostra em{" "}
              <strong className="text-tinta">todo curso</strong>, sem passar pelo par
              (produto, card). O que você mudar aqui alcança todo mundo.
            </>
          ) : card.produtos.length === 0 ? (
            <>
              Nenhum produto liga este card hoje, então ele não aparece para ninguém. Ligar é na
              Vitrine, por curso.
            </>
          ) : (
            <>
              Ligado a <strong className="text-tinta">{card.produtos.length} produto(s)</strong> em{" "}
              <strong className="text-tinta">{cursos.length} curso(s)</strong>:{" "}
              {cursos.join(", ")}. O que você mudar aqui vale para todos eles, não só para o curso
              de onde você veio.
            </>
          )}
        </p>
        {card.secoes.length > 0 && (
          <p className="mt-1 text-sm text-cinza">
            Em seção:{" "}
            {card.secoes.map((s) => `«${s.titulo}» (${s.curso_slug})`).join(", ")}
          </p>
        )}
      </section>

      {/* ── O link de acesso (F2, 000299) ───────────────────────────────── */}
      <LinkDeAcesso card={card} catalogo={catalogo} />

      {/* ── O uso (F10, 000303) ──────────────────────────────────────────── */}
      <UsoDaFicha slug={card.slug} />

      {/* ── Quem mantém, e onde o produto documenta isto ───────────────────── */}
      {/*
        O STATUS é editável desde 21/09 e continua sem coluna: o select escreve
        `ativo` e `destino`, que são o que a vitrine do aluno lê. Guardá-lo ao
        lado deles seria uma segunda verdade sobre a mesma coisa — o defeito do
        campo `tipo` do catálogo de CNAEs.

        O chip ao lado é a situação que está NO BANCO agora; o select é a que
        vai ser salva. Quando os dois divergem é porque há mudança pendente, e
        a tela mostra os dois de propósito.
      */}
      <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-quem-mantem">
        <h3 id="titulo-quem-mantem" className="font-display text-lg font-bold text-titulo">
          Quem mantém
        </h3>

        <p className="mt-3 text-sm text-cinza">
          Hoje ela está{" "}
          <span
            data-situacao={situacao.chave}
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${situacao.classe}`}
          >
            {situacao.rotulo}
          </span>{" "}
          — {situacao.diz}.
        </p>

        <div className="mt-5 flex flex-wrap items-end gap-3">
          <label className="flex min-w-60 flex-1 flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Status</span>
            <select
              data-campo="situacao"
              className={CAMPO}
              value={rascunho.situacao}
              onChange={(e) =>
                setRascunho({ ...rascunho, situacao: e.target.value as ChaveDaSituacao })
              }
            >
              {SITUACOES.map((s) => (
                <option key={s.chave} value={s.chave}>
                  {s.rotulo} — {s.diz}
                </option>
              ))}
            </select>
            {/* Os dois estados contraditórios são DITOS, em vez de a tela
                consertá-los sozinha. "Ativa" não tem endereço próprio: ela é
                qualquer destino que não seja `em-breve`, e escolher um por
                conta própria seria decidir por onde o aluno abre a ferramenta. */}
            {rascunho.situacao === "ativa" && rascunho.destino === "em-breve" ? (
              <span className="text-xs text-alerta-tinta" data-falta-destino>
                O destino ainda é «em-breve», então a tela do aluno continuaria dizendo que a
                ferramenta vem. Escolha o destino embaixo, em «O que o aluno vê», antes de salvar.
              </span>
            ) : (
              <span className="text-xs text-cinza">
                Salvar com «Salvar» ali embaixo — o status é o mesmo ato do nome e da descrição.
                Descontinuar pede o porquê, e o banco diz quantos alunos perdem a ferramenta.
              </span>
            )}
          </label>
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-3">
          <label className="flex min-w-60 flex-1 flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Responsável pela atualização</span>
            <input
              data-campo="responsavel"
              className={CAMPO}
              value={dono}
              onChange={(e) => setDono(e.target.value)}
              placeholder="Nome de quem mantém esta ferramenta"
            />
          </label>
          <button
            type="button"
            data-salvar-responsavel
            disabled={salvandoDono || dono === (card.responsavel ?? "")}
            onClick={() => void salvarDono()}
            className="btn-contorno min-h-12 px-5 text-sm disabled:opacity-55"
          >
            {salvandoDono ? "Salvando…" : "Definir"}
          </button>
        </div>
        <p className="mt-2 text-xs text-cinza">
          Em branco TIRA o responsável. As ferramentas nasceram sem dono de propósito — a página
          de governança do ClickUp também trazia esse campo por preencher —, e a tela inicial
          conta quantas ainda estão assim.
        </p>
        {recadoDono && (
          <div className="mt-4">
            <Recado tipo={recadoDono.tipo} texto={recadoDono.texto} />
          </div>
        )}

        {/* A PROCEDÊNCIA. O texto de baixo é da casa, mas quem documenta a
            ferramenta para vender e ensinar é a PÁGINA DO PRODUTO — e quem for
            reescrever o texto deveria ler a de lá primeiro. */}
        {paginas.length > 0 && (
          <p className="mt-5 border-t border-borda pt-4 text-sm text-cinza" data-paginas-do-produto>
            Esta ferramenta é documentada na página do produto de{" "}
            {paginas.map((p, i) => (
              <span key={p.curso_slug}>
                {i > 0 && (i === paginas.length - 1 ? " e " : ", ")}
                <Link
                  to={`/admin/catalogo/produtos?produto=${encodeURIComponent(p.curso_slug)}`}
                  className="font-semibold text-link underline underline-offset-2"
                >
                  {p.nome}
                </Link>
              </span>
            ))}
            .
          </p>
        )}
      </section>

      {/* ── O documento de divulgação ──────────────────────────────────────── */}
      {/*
        Pedido de Victor em 21/09: gerar por IA, a partir do que está cadastrado,
        um documento de divulgação por ferramenta — e um BOTÃO que execute isso
        manualmente. Manual é decisão: cada rodada custa uma chamada ao modelo, e
        um gatilho em `update cards` faria a casa pagar por uma vírgula
        corrigida na descrição.

        O texto é MARKDOWN e é desenhado pela `Prosa`, que limpa a marcação na
        apresentação. O hub não tem `dangerouslySetInnerHTML` em lugar nenhum, e
        a ausência é a peça de segurança — aqui ainda mais, porque este texto foi
        escrito por um modelo.
      */}
      <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-divulgacao">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 id="titulo-divulgacao" className="font-display text-lg font-bold text-titulo">
              Documento de divulgação
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-cinza">
              Escrito por IA a partir do que está cadastrado aqui — descrição, produtos
              vinculados, como o aluno acessa e a última atualização. Serve de apoio para vendas
              e para as outras áreas. Quem manda gerar é você; nada gera sozinho.
            </p>
          </div>
          <button
            type="button"
            data-gerar-divulgacao
            disabled={gerando}
            onClick={() => void gerarODoc()}
            className="pill-cta min-h-12 shrink-0 px-5 text-sm disabled:opacity-55"
          >
            {gerando ? "Gerando…" : doc?.existe ? "Gerar de novo" : "Gerar documento"}
          </button>
        </div>

        {recadoDoc && (
          <div className="mt-4">
            <Recado tipo={recadoDoc.tipo} texto={recadoDoc.texto} />
          </div>
        )}

        {doc?.existe ? (
          <div className="mt-5" data-divulgacao>
            {/* A PROCEDÊNCIA vem antes do texto, e não depois: quem lê precisa
                saber que isto foi escrito por um modelo ANTES de começar a
                confiar nas frases. */}
            <p className="text-xs text-cinza" data-divulgacao-procedencia>
              Gerado por IA ({doc.modelo}) em{" "}
              {doc.gerado_em ? new Date(doc.gerado_em).toLocaleString("pt-BR") : "—"}
              {doc.gerado_por_nome ? `, a pedido de ${doc.gerado_por_nome}` : ""}. Revise antes de
              mandar para fora.
            </p>

            {/* O documento envelhece sozinho quando alguém edita a ferramenta, e
                a tela DIZ quais campos mudaram — "está desatualizado" sem dizer
                o quê não muda a conduta de ninguém. */}
            {doc.desatualizado && (
              <p
                data-divulgacao-desatualizado
                className="mt-3 rounded-cf bg-alerta-fundo px-3 py-2 text-sm text-alerta-tinta"
              >
                A ferramenta mudou depois deste documento
                {doc.mudou?.length ? ` (${doc.mudou.join(", ")})` : ""}. Gere de novo antes de
                usar.
              </p>
            )}

            <div className="mt-4 rounded-cf border border-borda bg-tile p-4">
              <Prosa texto={doc.texto ?? ""} />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {/* COPIAR é o gesto central, como no Leitor de Processos: este
                  texto existe para virar um doc, um e-mail ou um roteiro de
                  call. O que vai para a área de transferência é o MARKDOWN
                  cru, não o texto limpo da tela — ele é o que cola formatado do
                  outro lado. */}
              <button
                type="button"
                data-copiar-divulgacao
                onClick={() => {
                  void navigator.clipboard
                    .writeText(doc.texto ?? "")
                    .then(() => setCopiou(true))
                    .catch(() => setCopiou(false));
                }}
                className="btn-contorno min-h-10 text-sm"
              >
                {copiou ? "Copiado" : "Copiar"}
              </button>
              <span className="text-xs text-cinza">
                {(doc.texto ?? "").length.toLocaleString("pt-BR")} caracteres
                {doc.tokens_in != null && doc.tokens_out != null
                  ? ` · ${doc.tokens_in + doc.tokens_out} tokens nesta geração`
                  : ""}
              </span>
            </div>
          </div>
        ) : (
          /* A ausência é RESPOSTA, e ela diz o que falta para o botão
             funcionar — a ferramenta sem texto nenhum é recusada pelo banco, e
             descobrir isso só ao clicar seria uma ida perdida. */
          <p className="mt-4 text-sm text-cinza" data-sem-divulgacao>
            Ainda não há documento para esta ferramenta.{" "}
            {!card.sobre?.trim() && !card.descricao?.trim()
              ? "E ele não sai de nada: escreva a descrição ou o «o que é esta ferramenta» antes de gerar."
              : "Clique em «Gerar documento» — leva alguns segundos."}
          </p>
        )}
      </section>

      {/* ── A última atualização ───────────────────────────────────────────── */}
      {/*
        Os dois campos que faltavam na ficha da equipe de produtos (000268). Eles
        não saem do razão de mudanças, e a razão está na migration: o razão
        registra quem mexeu no CADASTRO, e estes dois registram o que mudou na
        FERRAMENTA. As duas coisas divergem nos dois sentidos — o Radar ganhou a
        bancada de cálculo sem o card mudar uma vírgula, e corrigir um acento na
        descrição não é uma atualização da ferramenta.
      */}
      <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-atualizacao">
        <h3 id="titulo-atualizacao" className="font-display text-lg font-bold text-titulo">
          Atualizações da ferramenta
        </h3>
        <p className="mt-1 text-sm text-cinza">
          O que a ferramenta ganhou de novo, e quando. É o que responde «esta ferramenta ainda
          está em dia?» sem alguém ter de abrir e olhar — e, desde 22/09, <strong>cada registro
          fica</strong>: a tabela embaixo é a história dela.
        </p>

        {/* Os DOIS ramos são ditos: sem o de baixo, a ausência de data se lê
            como "ninguém precisou atualizar", que é o oposto do que ela
            significa. É a lição do "Limite com a prorrogação" do HUB DP. */}
        <p className="mt-3 text-sm text-cinza" data-atualizacao-hoje={card.atualizada_em ?? ""}>
          {card.atualizada_em
            ? `No banco: ${card.atualizada_em.split("-").reverse().join("/")}.`
            : "Nenhuma atualização registrada até agora."}
        </p>

        {/* A DATA AUTOMÁTICA (000304). Os três ramos são ditos: sem tabela de
            conteúdo, com tabela e sem data, e com data — o silêncio seria lido
            como "o conteúdo nunca mudou". */}
        {conteudo && (
          <div className="mt-2 text-sm text-cinza" data-conteudo-automatico={conteudo.ultima ?? ""}>
            {!conteudo.tem_conteudo ? (
              <p>
                Esta ferramenta não tem conteúdo editável pela casa: a data dela vem só dos registros
                abaixo.
              </p>
            ) : !conteudo.ultima ? (
              <p>
                O conteúdo dela ainda não tem data — as tabelas não guardam, e nenhuma edição foi
                registrada desde 25/09/2026.
              </p>
            ) : (
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>
                  O conteúdo mudou pela última vez em{" "}
                  <strong className="font-semibold text-tinta" data-data-do-conteudo>
                    {new Date(conteudo.ultima).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}
                  </strong>
                  , em «{conteudo.ultima_em}» — automático.
                </span>
                <button
                  type="button"
                  data-usar-data-do-conteudo
                  onClick={() =>
                    setAtualizacao({
                      ...atualizacao,
                      // `en-CA` escreve AAAA-MM-DD, que é o que o campo de data
                      // aceita — e no fuso de São Paulo, como o banco usa.
                      quando: new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(
                        new Date(conteudo.ultima!),
                      ),
                    })
                  }
                  className="text-xs font-semibold text-link underline-offset-2 hover:underline"
                >
                  Usar esta data no registro
                </button>
              </p>
            )}
            {conteudo.tem_conteudo && (
              <details className="mt-1.5">
                <summary className="cursor-pointer text-xs font-semibold text-cinza">
                  De onde vem esta data
                </summary>
                <ul className="mt-1.5 space-y-0.5 text-xs" data-fontes-do-conteudo={conteudo.fontes.length}>
                  {conteudo.fontes.map((f) => (
                    <li key={f.tabela}>
                      {f.rotulo}:{" "}
                      {f.ultima
                        ? new Date(f.ultima).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })
                        : "sem data"}
                      {!f.guarda_data && " (a tabela não guarda data — conta desde 25/09/2026, pelas edições)"}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="mt-5 grid gap-4 sm:grid-cols-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Data</span>
            <input
              type="date"
              data-campo="atualizada-em"
              className={CAMPO}
              value={atualizacao.quando}
              onChange={(e) => setAtualizacao({ ...atualizacao, quando: e.target.value })}
            />
            <span className="text-xs text-cinza">
              Em branco, o banco usa hoje — no fuso de São Paulo, não no do navegador.
            </span>
          </label>

          <label className="flex flex-col gap-1.5 sm:col-span-3">
            <span className="text-sm font-semibold text-tinta">O que mudou</span>
            <textarea
              data-campo="atualizacao-nota"
              rows={3}
              className={`${CAMPO} min-h-20 resize-y`}
              value={atualizacao.nota}
              onChange={(e) => setAtualizacao({ ...atualizacao, nota: e.target.value })}
              placeholder="Ex.: passou a aceitar o CNAE secundário e a mostrar o Fator R na fronteira."
            />
          </label>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-salvar-atualizacao
            disabled={salvandoAtualizacao || atualizacao.nota.trim() === ""}
            onClick={() => void salvarAtualizacao()}
            className="btn-contorno min-h-12 px-5 text-sm disabled:opacity-55"
          >
            {salvandoAtualizacao ? "Registrando…" : "Registrar atualização"}
          </button>
          <p className="text-xs text-cinza">
            Cada registro ACRESCENTA uma linha ao histórico — não substitui a anterior. Data no
            futuro o banco recusa; para desfazer um registro errado, remova a linha dele na
            tabela abaixo.
          </p>
        </div>

        {recadoAtualizacao && (
          <div className="mt-4">
            <Recado tipo={recadoAtualizacao.tipo} texto={recadoAtualizacao.texto} />
          </div>
        )}

        {/* ── O histórico (000277) ──────────────────────────────────────────
            Tabela, e não lista de cartões: o uso é comparar datas e ler o que
            mudou em cada uma, e para isso linha encontra melhor — a cicatriz do
            IR Pro (8.249px para 1.120px com o mesmo conteúdo) e a da busca de
            CNAE (50 cartões de 170px para responder o que uma linha responde). */}
        <div className="mt-6 border-t border-borda pt-5">
          <h4 className="text-sm font-semibold text-tinta">
            Histórico{historico.length > 0 && ` — ${historico.length} registro(s)`}
          </h4>

          {historico.length === 0 ? (
            /* Os DOIS ramos são ditos. Sem esta frase, a ausência de tabela se
               lê como "a ferramenta nunca mudou", que é o oposto do que ela
               significa — é a lição do "Limite com a prorrogação" do HUB DP. */
            <p className="mt-2 text-sm text-cinza" data-historico-vazio>
              Nenhuma atualização registrada até agora. Isto não quer dizer que a ferramenta não
              mudou — quer dizer que ninguém registrou.
            </p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm" data-historico-atualizacoes={historico.length}>
                <thead>
                  <tr className="border-b border-borda text-left text-xs text-cinza">
                    <th className="py-2 pr-3 font-semibold">Data</th>
                    <th className="py-2 pr-3 font-semibold">O que mudou</th>
                    <th className="py-2 pr-3 font-semibold">Registrado por</th>
                    {/* O `sr-only` vai no SPAN, nunca na `<th>`: ele é
                        `position: absolute`, e aplicado à célula a tiraria do
                        fluxo da tabela — a coluna perderia largura e o
                        alinhamento das outras quatro sairia junto. */}
                    <th className="py-2 font-semibold">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-borda">
                  {historico.map((a) => (
                    <tr key={a.id} data-atualizacao={a.id} className="align-top">
                      <td className="whitespace-nowrap py-2.5 pr-3 text-tinta">
                        {a.quando.split("-").reverse().join("/")}
                      </td>
                      <td className="py-2.5 pr-3 text-tinta">
                        {a.nota}
                        {a.divulgada_agora && a.divulgada_ate ? (
                          <span className="mt-1 block text-xs text-aviso-tinta" data-divulgada={a.id}>
                            Na área do aluno até{" "}
                            {new Date(a.divulgada_ate).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}:
                            «{a.aviso_ao_aluno}»
                          </span>
                        ) : a.divulgada_em && a.divulgada_ate ? (
                          <span className="mt-1 block text-xs text-cinza">
                            Esteve na área do aluno até{" "}
                            {new Date(a.divulgada_ate).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}.
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap py-2.5 pr-3 text-cinza">{a.por ?? "—"}</td>
                      <td className="py-2.5 text-right">
                        <div className="flex flex-col items-end gap-1.5">
                        {a.divulgada_agora ? (
                          <button
                            type="button"
                            data-retirar-divulgacao={a.id}
                            onClick={() => {
                              setRecadoDivulgacao(null);
                              setDivulgando(null);
                              setRetirando({ id: a.id, motivo: "" });
                            }}
                            className="text-xs font-semibold text-cinza underline-offset-2 transition hover:text-tinta hover:underline"
                          >
                            Retirar do aluno
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-divulgar-atualizacao={a.id}
                            onClick={() => {
                              setRecadoDivulgacao(null);
                              setRetirando(null);
                              // O texto nasce do AVISO anterior, se houve, e senão
                              // da nota: é o ponto de partida, e a pessoa o
                              // reescreve com as palavras de quem usa a ferramenta.
                              setDivulgando({ id: a.id, texto: a.aviso_ao_aluno ?? a.nota });
                            }}
                            className="text-xs font-semibold text-link underline-offset-2 transition hover:underline"
                          >
                            {a.divulgada_em ? "Divulgar de novo" : "Divulgar ao aluno"}
                          </button>
                        )}
                        <button
                          type="button"
                          data-remover-atualizacao={a.id}
                          onClick={() => {
                            setRecadoAtualizacao(null);
                            setRemovendo({ id: a.id, motivo: "" });
                          }}
                          className="text-xs font-semibold text-cinza underline-offset-2 transition hover:text-tinta hover:underline"
                        >
                          Remover
                        </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {removendo && (
            <div className="mt-4 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo p-3">
              <p className="text-sm text-alerta-tinta">
                Tirar esta linha do histórico. Ela some da tabela, e se for a mais recente a data
                da ferramenta volta a ser a da anterior — ou nenhuma, se não houver outra.
              </p>
              <label className="mt-3 flex flex-col gap-1.5">
                <span className="text-sm font-semibold text-alerta-tinta">
                  Justifique a alteração
                </span>
                <input
                  data-campo="motivo-remocao"
                  className={CAMPO}
                  value={removendo.motivo}
                  onChange={(e) => setRemovendo({ ...removendo, motivo: e.target.value })}
                  placeholder="Vai para o razão de mudanças"
                />
              </label>
              <div className="mt-3 flex flex-wrap gap-2">
                {/* Sem `disabled` por motivo vazio: quem decide se ele é
                    obrigatório é o BANCO, e a recusa dele vem com a frase. */}
                <button
                  type="button"
                  data-confirmar-remocao
                  disabled={salvandoRemocao}
                  onClick={() => void removerDoHistorico()}
                  className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55"
                >
                  {salvandoRemocao ? "Removendo…" : "Remover"}
                </button>
                <button
                  type="button"
                  onClick={() => setRemovendo(null)}
                  className="btn-contorno min-h-10 text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {divulgando && (
            <div className="mt-4 rounded-cf border border-borda p-3" data-form-divulgar={divulgando.id}>
              <p className="text-sm text-tinta">
                Divulgar esta atualização ao aluno. Quem tem a ferramenta vê o aviso por 30 dias —
                como «Novidade» no card, na página do curso, e no alto da ferramenta. Não sai e-mail
                nem notificação.
              </p>
              <label className="mt-3 flex flex-col gap-1.5">
                <span className="text-sm font-semibold text-tinta">O que o aluno lê</span>
                <textarea
                  data-campo="aviso-ao-aluno"
                  rows={2}
                  className={`${CAMPO} min-h-16 resize-y`}
                  value={divulgando.texto}
                  onChange={(e) => setDivulgando({ ...divulgando, texto: e.target.value })}
                />
                {/* O contador AVISA; quem recusa acima de 280 é o banco, com a
                    frase dele — e por isso o campo não corta o texto. */}
                <span
                  className={`text-xs ${divulgando.texto.trim().length > 280 ? "text-erro-tinta" : "text-cinza"}`}
                  data-contador-do-aviso
                >
                  {divulgando.texto.trim().length} de 280 caracteres
                </span>
              </label>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-confirmar-divulgacao
                  disabled={salvandoDivulgacao}
                  onClick={() => void divulgarAoAluno()}
                  className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55"
                >
                  {salvandoDivulgacao ? "Divulgando…" : "Divulgar"}
                </button>
                <button type="button" onClick={() => setDivulgando(null)} className="btn-contorno min-h-10 text-sm">
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {retirando && (
            <div className="mt-4 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo p-3" data-form-retirar={retirando.id}>
              <p className="text-sm text-alerta-tinta">
                Tirar esta novidade da área do aluno. O aviso fica guardado aqui, para uma nova
                divulgação.
              </p>
              <label className="mt-3 flex flex-col gap-1.5">
                <span className="text-sm font-semibold text-alerta-tinta">Justifique a alteração</span>
                <input
                  data-campo="motivo-retirada"
                  className={CAMPO}
                  value={retirando.motivo}
                  onChange={(e) => setRetirando({ ...retirando, motivo: e.target.value })}
                  placeholder="Vai para o razão de mudanças"
                />
              </label>
              <div className="mt-3 flex flex-wrap gap-2">
                {/* Sem `disabled` por motivo vazio: quem decide se ele é
                    obrigatório é o BANCO, e a recusa dele vem com a frase. */}
                <button
                  type="button"
                  data-confirmar-retirada
                  disabled={salvandoDivulgacao}
                  onClick={() => void retirarDoAluno()}
                  className="pill-cta min-h-10 px-5 text-sm disabled:opacity-55"
                >
                  {salvandoDivulgacao ? "Retirando…" : "Retirar"}
                </button>
                <button type="button" onClick={() => setRetirando(null)} className="btn-contorno min-h-10 text-sm">
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {recadoDivulgacao && (
            <div className="mt-2" data-recado-divulgacao>
              <Recado tipo={recadoDivulgacao.tipo} texto={recadoDivulgacao.texto} />
            </div>
          )}
        </div>
      </section>

      {/* ── O que É esta ferramenta ────────────────────────────────────────── */}
      <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-sobre">
        <h3 id="titulo-sobre" className="font-display text-lg font-bold text-titulo">
          O que é esta ferramenta
        </h3>
        <p className="mt-1 text-sm text-cinza">
          Para quem OPERA — nenhum aluno vê este texto. É onde fica o que a ferramenta faz, para
          quem serve e o que ela não faz, para a próxima pessoa não precisar abrir o código.
        </p>
        <textarea
          data-campo="sobre"
          rows={8}
          className={`${CAMPO} mt-4 min-h-40 resize-y`}
          value={rascunho.sobre}
          onChange={(e) => setRascunho({ ...rascunho, sobre: e.target.value })}
          placeholder="Ex.: calcula o DAS do MEI mês a mês e monta o boleto. Serve ao aluno do MEI PRO e ao da Pós Rotinas. Não emite guia oficial — o número sai do simulador."
        />
        {/* O contrato dos nulos é do banco, e vale a pena estar dito: um texto
            longo apagado por omissão não tem volta pela interface. */}
        <p className="mt-2 text-xs text-cinza">
          Em branco APAGA o texto. É o contrato das RPCs do catálogo: nulo não mexe, vazio limpa.
        </p>
      </section>

      {/* ── O que o aluno vê ───────────────────────────────────────────────── */}
      <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-do-aluno">
        <h3 id="titulo-do-aluno" className="font-display text-lg font-bold text-titulo">
          O que o aluno vê
        </h3>
        <p className="mt-1 text-sm text-cinza">
          O nome e a linha embaixo dele, na vitrine do curso. Mexer aqui muda a tela de quem
          pagou.
        </p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-sm font-semibold text-tinta">Nome</span>
            <input
              ref={primeiro}
              data-campo="nome"
              className={CAMPO}
              value={rascunho.nome}
              onChange={(e) => setRascunho({ ...rascunho, nome: e.target.value })}
            />
          </label>

          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-sm font-semibold text-tinta">Descrição</span>
            <input
              data-campo="descricao"
              className={CAMPO}
              value={rascunho.descricao}
              onChange={(e) => setRascunho({ ...rascunho, descricao: e.target.value })}
              placeholder="A linha embaixo do nome, na tela do curso"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Destino</span>
            <select
              data-campo="destino"
              className={CAMPO}
              value={rascunho.destino}
              onChange={(e) => setRascunho({ ...rascunho, destino: e.target.value })}
            >
              {DESTINOS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            {/* As seis opções ficam todas aqui. Quem recusa é o banco, e a
                recusa dele EXPLICA — esconder as quatro de código deixaria o
                operador sem saber por que elas não estão. E a do card tem de
                estar na lista: um valor fora dela faz o select MOSTRAR a
                primeira opção ("em-breve") num card que não está em breve. */}
            <span className="text-xs text-cinza">
              Quem recusa é o banco, e a recusa diz o porquê de cada uma.
            </span>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Endereço</span>
            <input
              data-campo="url"
              className={CAMPO}
              value={rascunho.url}
              onChange={(e) => setRascunho({ ...rascunho, url: e.target.value })}
              placeholder="https://…"
            />
            <span className="text-xs text-cinza">
              Só o destino «url» usa. Ele vira o <code>href</code> do aluno, e o banco confere se
              é um endereço da casa.
            </span>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-tinta">Ordem</span>
            <input
              data-campo="ordem"
              className={CAMPO}
              value={rascunho.ordem}
              inputMode="numeric"
              onChange={(e) =>
                setRascunho({ ...rascunho, ordem: e.target.value.replace(/\D/g, "").slice(0, 4) })
              }
            />
          </label>

          {/*
            NÃO há mais a caixinha "Card ativo": ela virou o select de STATUS,
            lá em cima (21/09). Duas formas de escrever o mesmo `cards.ativo` na
            mesma tela é o defeito das "duas portas para a mesma ação" — e aqui
            a segunda porta era a pior das duas, porque desmarcar uma caixinha
            não parece o que é: tirar a ferramenta da tela de quem pagou.
          */}

          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-sm font-semibold text-tinta">Justifique a alteração</span>
            <input
              data-campo="motivo"
              className={CAMPO}
              value={rascunho.motivo}
              onChange={(e) => setRascunho({ ...rascunho, motivo: e.target.value })}
              placeholder="Vai para o registro de mudanças, ao lado do que mudou"
            />
            {/* A tela NÃO decide quando o motivo é obrigatório — quem sabe
                quantos alunos o ato alcança é o banco, que devolve o número na
                recusa. Duplicar a regra aqui criaria a segunda definição dela. */}
            <span className="text-xs text-cinza">
              Alguns atos só passam com o porquê escrito. Se este for um deles, o banco diz — e diz
              também quantos alunos ele alcança.
            </span>
          </label>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-salvar-ferramenta
            disabled={salvando}
            onClick={() => void salvar()}
            className="pill-cta min-h-12 px-5 text-sm disabled:opacity-55"
          >
            {salvando ? "Salvando…" : "Salvar"}
          </button>
          <a href={voltar} className="btn-contorno inline-flex min-h-10 items-center text-sm">
            Cancelar
          </a>
        </div>

        {/* O recado fica NESTA seção, junto do botão que o produziu: erro de uma
            escrita não pode trocar a tela inteira. */}
        {recado && (
          <div className="mt-4">
            <Recado tipo={recado.tipo} texto={recado.texto} />
          </div>
        )}
      </section>

      {/* ── O histórico de mudanças (F7, 000299) ──────────────────────────── */}
      <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-mudancas">
        <h3 id="titulo-mudancas" className="font-display text-lg font-bold text-titulo">
          Histórico de mudanças
        </h3>
        <p className="mt-1 text-sm text-cinza">
          Quem mexeu no cadastro desta ferramenta por este painel, quando e por quê — gravado pelo
          banco a cada ato. Não é o mesmo que as «Atualizações» acima: aquelas contam o que mudou
          na ferramenta; esta conta o que mudou no cadastro dela.
        </p>

        {mudancas === null ? (
          <p className="mt-4 text-sm text-cinza">Carregando o histórico…</p>
        ) : mudancas.length === 0 ? (
          /* Os DOIS ramos são ditos: sem esta frase, a ausência de tabela se
             lê como "o histórico não funciona". */
          <p className="mt-4 text-sm text-cinza" data-mudancas-vazio>
            Nenhuma mudança registrada no cadastro desta ferramenta. O registro começou em 16/09;
            o que foi feito antes disso, por migration, não passou por ele.
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-historico-de-mudancas={totalDeMudancas}>
              <thead>
                <tr className="border-b border-borda text-left text-xs text-cinza">
                  <th className="py-2 pr-3 font-semibold">Quando</th>
                  <th className="py-2 pr-3 font-semibold">O que aconteceu</th>
                  <th className="py-2 pr-3 font-semibold">Por quê</th>
                  <th className="py-2 font-semibold">Quem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {mudancas.map((m) => (
                  <tr key={m.id} data-mudanca={m.id} className="align-top">
                    <td className="whitespace-nowrap py-2.5 pr-3 text-cinza">
                      {new Date(m.em).toLocaleString("pt-BR", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </td>
                    <td className="py-2.5 pr-3 text-tinta" data-resumo>
                      {m.resumo}
                    </td>
                    <td className="py-2.5 pr-3 text-cinza">{m.motivo ?? "—"}</td>
                    <td className="whitespace-nowrap py-2.5 text-cinza">{m.por}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalDeMudancas > mudancas.length && (
              <p className="mt-3 text-xs text-cinza">
                Mostrando as {mudancas.length} mais recentes de {totalDeMudancas}.
              </p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * O USO da ferramenta nos últimos 30 dias (F10, 000303), com ALUNOS e EQUIPE
 * separados. A separação é o que impede o número de mentir enquanto o hub não
 * está aberto aos alunos: todo uso de hoje é da equipe, e um total único leria
 * "ninguém usa" ou "todo mundo usa" conforme a semana de testes.
 *
 * E a tela diz DESDE QUANDO conta: as ferramentas que moram aqui só passaram a
 * registrar abertura em 25/09. Zero antes disso não é "ninguém abriu".
 */
type EstadoDoUso =
  | { fase: "carregando" }
  | { fase: "erro"; detalhe: string }
  // O agregado mede ferramenta, curso bônus e porta; os MÓDULOS da FBC ficam
  // fora (a rota deles é /faculdade). Sem este estado, a ficha de um módulo
  // aberta pela URL diria "Carregando…" para sempre.
  | { fase: "fora" }
  | { fase: "pronto"; uso: UsoDaFerramenta };

function UsoDaFicha({ slug }: { slug: string }) {
  const [estado, setEstado] = useState<EstadoDoUso>({ fase: "carregando" });
  useEffect(() => {
    let vivo = true;
    setEstado({ fase: "carregando" });
    void (async () => {
      const r = await lerUsoDasFerramentas(30);
      if (!vivo) return;
      if (!r.ok) {
        setEstado({ fase: "erro", detalhe: r.detalhe ?? "O uso não veio — tente recarregar a página." });
        return;
      }
      const uso = r.ferramentas.find((f) => f.slug === slug);
      setEstado(uso ? { fase: "pronto", uso } : { fase: "fora" });
    })();
    return () => {
      vivo = false;
    };
  }, [slug]);

  return (
    <section
      className="surface rise p-6 sm:p-7"
      role="group"
      aria-labelledby="titulo-uso"
      data-uso-da-ferramenta={estado.fase}
    >
      <h3 id="titulo-uso" className="font-display text-lg font-bold text-titulo">
        Uso nos últimos 30 dias
      </h3>
      {estado.fase === "carregando" ? (
        <p className="mt-2 text-sm text-cinza">Carregando…</p>
      ) : estado.fase === "erro" ? (
        <p className="mt-2 text-sm text-cinza" role="alert">
          {estado.detalhe}
        </p>
      ) : estado.fase === "fora" ? (
        <p className="mt-2 text-sm text-cinza">
          Este card não entra na medição de uso — ela conta ferramentas, cursos bônus e portas.
        </p>
      ) : (
        <>
          <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <dt className="text-xs text-cinza">Alunos</dt>
              <dd className="font-display text-2xl font-bold text-titulo" data-uso-alunos>
                {estado.uso.alunos}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-cinza">Equipe</dt>
              <dd className="font-display text-2xl font-bold text-titulo" data-uso-equipe>
                {estado.uso.equipe}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-cinza">Aberturas</dt>
              <dd className="font-display text-2xl font-bold text-titulo" data-uso-aberturas>
                {estado.uso.aberturas}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-cinza">Última</dt>
              <dd className="text-sm font-semibold text-tinta">
                {estado.uso.ultima ? new Date(estado.uso.ultima).toLocaleDateString("pt-BR") : "—"}
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-cinza" data-uso-nota>
            Alunos e equipe contam pessoas; aberturas contam entradas, sem a recarga da mesma página em 5
            minutos.{" "}
            {estado.uso.destino === "interno" || estado.uso.destino === "url"
              ? "Esta ferramenta passou a registrar abertura em 25/09/2026 — antes disso, zero não quer dizer que ninguém abriu."
              : "Ela abre pela ponte do hub, que registra desde 19/08/2026."}
            {estado.uso.negadas === 1
              ? " 1 tentativa de quem não tinha a ferramenta."
              : estado.uso.negadas > 1
                ? ` ${estado.uso.negadas} tentativas de quem não tinha a ferramenta.`
                : ""}
          </p>
        </>
      )}
    </section>
  );
}

/**
 * O LINK DE ACESSO da ferramenta (F2, 000299), por curso.
 *
 * Derivado por `aberturaDoCard` — a função que a vitrine do aluno usa para
 * montar o `href` de cada card —, e NUNCA remontado aqui. É a regra de "uma
 * definição para as seis telas de card": se o painel montasse o endereço por
 * conta própria, a primeira diferença entre os dois seria um link que a equipe
 * manda a alguém e que não é o que o aluno recebe (os dois módulos da FBC, por
 * exemplo, não moram em `/ferramenta/<slug>`, e só `rotaDaFerramenta` sabe).
 *
 * O que ele NÃO faz é decidir quem entra: o link abre para quem tem o curso, e
 * quem confere é o banco, na chegada. Por isso a tela diz isso ao lado.
 */
function LinkDeAcesso({ card, catalogo }: { card: CardDoCatalogo; catalogo: Catalogo }) {
  const [copiado, setCopiado] = useState<string | null>(null);
  const origem = typeof window === "undefined" ? "" : window.location.origin;

  // O card do CATÁLOGO na forma que a regra da vitrine lê. `trancado` vai
  // falso de propósito: aqui a pergunta é "por onde abre", e a trava de
  // contrato é do aluno, não da ferramenta.
  const comoVitrine: CardVitrine = {
    card_slug: card.slug,
    card_nome: card.nome,
    card_tipo: card.tipo,
    card_escopo: card.escopo,
    descricao: card.descricao,
    url: card.url,
    destino: card.destino,
    universal: card.universal,
    ordem: card.ordem,
    por_produtos: [],
    por_cursos: [],
    trancado: false,
  };

  const nomeDoCurso = (slug: string) => catalogo.cursos.find((c) => c.slug === slug)?.nome || slug;
  // Universal vale em todo curso; de conta, em nenhum (é uma vez na inicial).
  const cursos: Array<string | null> =
    card.escopo === "conta"
      ? [null]
      : card.universal
        ? catalogo.cursos.map((c) => c.slug)
        : [...new Set(card.produtos.map((p) => p.curso_slug))];

  const linhas = cursos
    .map((curso) => {
      const a = aberturaDoCard(comoVitrine, curso);
      if (!a.abre) return null;
      const href = a.href.startsWith("http") ? a.href : `${origem}${a.href}`;
      return { chave: curso ?? "conta", rotulo: curso ? nomeDoCurso(curso) : "Tela inicial", href };
    })
    .filter((l): l is { chave: string; rotulo: string; href: string } => Boolean(l));
  // Destino `url` é o MESMO endereço em todo curso: uma linha basta.
  const unicas = card.destino === "url" ? linhas.slice(0, 1) : linhas;

  async function copiar(chave: string, texto: string) {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(chave);
    } catch {
      setCopiado(null);
    }
  }

  return (
    <section
      className="surface rise p-6 sm:p-7"
      role="group"
      aria-labelledby="titulo-link-de-acesso"
      data-link-de-acesso={card.destino}
    >
      <h3 id="titulo-link-de-acesso" className="font-display text-lg font-bold text-titulo">
        Link de acesso
      </h3>

      {card.destino === "em-breve" ? (
        <p className="mt-2 text-sm text-cinza">
          Ainda não abre: a ferramenta está <strong className="text-tinta">em construção</strong>, e
          o aluno vê o card dizendo que ela ainda vem. O link aparece aqui quando o destino mudar —
          e isso depende do porte dela (uma rota no hub, ou a credencial de outro sistema).
        </p>
      ) : unicas.length === 0 ? (
        <p className="mt-2 text-sm text-cinza">
          Nenhum curso liga esta ferramenta, então não há por onde o aluno chegar a ela. O link
          aparece aqui, por curso, quando ela for ligada — na lista de Ferramentas.
        </p>
      ) : (
        <>
          <p className="mt-1 text-sm text-cinza">
            {card.destino === "interno"
              ? "A ferramenta mora no hub. O curso vai no endereço porque é ele que decide o botão de voltar — e, nas IAs, qual material responde."
              : card.destino === "url"
                ? "Um endereço fixo, igual para todo curso."
                : "Abre em outro sistema, com uma credencial gerada no clique (o link do Memberkit vale 24 h). O endereço abaixo é o da PONTE do hub, que é o que se pode mandar a alguém."}{" "}
            O link abre para quem tem o curso: quem confere é o banco, na chegada.
          </p>
          {!card.ativo && (
            <p className="mt-3 rounded-cf bg-alerta-fundo px-3 py-2 text-sm text-alerta-tinta">
              A ferramenta está desativada: o card não aparece na vitrine de ninguém, e estes links
              não abrem enquanto ela estiver assim.
            </p>
          )}
          <ul className="mt-4 divide-y divide-borda rounded-cf border border-borda">
            {unicas.map((l) => (
              <li
                key={l.chave}
                className="flex flex-wrap items-center gap-3 p-3"
                data-link-do-curso={l.chave}
              >
                <span className="min-w-40 text-sm font-semibold text-tinta">
                  {card.destino === "url" ? "Todos os cursos" : l.rotulo}
                </span>
                <code className="min-w-0 flex-1 break-all text-xs text-cinza" data-href>
                  {l.href}
                </code>
                <button
                  type="button"
                  data-copiar-link={l.chave}
                  onClick={() => void copiar(l.chave, l.href)}
                  className="btn-contorno min-h-9 px-3 text-xs"
                >
                  {copiado === l.chave ? "Copiado" : "Copiar"}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
